import type { LabelInValue } from 'models';
import { Flex, Form, FormActions, Modal, ProgressIndicator, RichTextInput, Text, Title } from 'ui';
import { useUser } from 'features/auth';
import type { LabelInValueWithTranslations } from '../../../types';
import { useOnFormFinish } from './hooks';
import { TelehealthOptions } from './telehealth-options';

export type TelehealthRequestFormValues = {
  htmlContent: string;
  medicalGroup?: LabelInValue;
  textContent: string;
  visitType: LabelInValueWithTranslations;
  visitTypeSubOption?: LabelInValueWithTranslations;
  visitTypeSubSubOption?: LabelInValueWithTranslations;
};

type P = {
  initialFormValues?: Partial<TelehealthRequestFormValues>;
  onBack: (formValues: Partial<TelehealthRequestFormValues>) => void;
  onSuccess: () => void;
};

const TelehealthRequestContent = ({ initialFormValues, onBack, onSuccess }: P) => {
  const [form] = Form.useForm<TelehealthRequestFormValues>();
  const { isLoading, onFormFinish } = useOnFormFinish();
  const { nodeUserData } = useUser();
  const userMedicalGroupMemberships = nodeUserData.memberships?.filter(
    (membership) => membership.type === 'group.medicalGroup' && membership.role === 'member',
  );

  const onFinish = async (values: TelehealthRequestFormValues) =>
    onFormFinish({ data: values, onSuccess, userMedicalGroupMemberships });

  return (
    <Form<TelehealthRequestFormValues>
      form={form}
      initialValues={initialFormValues}
      name="telehealth-request-form"
      onFinish={onFinish}
    >
      <Flex gap="large" orientation="vertical">
        <ProgressIndicator
          progressProperties={{ percent: 90, size: 'extra-small' }}
          step={3}
          textProperties={{ type: 'title' }}
          total={3}
        />
        <Title level={3}>Visit details</Title>
      </Flex>
      <TelehealthOptions<TelehealthRequestFormValues> userMedicalGroupMemberships={userMedicalGroupMemberships} />
      <RichTextInput
        html={{
          'data-test-id': 'event_description',
          extra: (
            <Text size="small" type="secondary">
              Info: Tell us the reason for your visit and your preferred date, time, and duration of the appointment.
            </Text>
          ),
          label: 'Why do you need consultation',
          name: 'htmlContent',
          rules: [{ required: true }],
        }}
        text={{ name: 'textContent' }}
      />
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Back',
            onClick() {
              onBack(form.getFieldsValue());
            },
          }}
          submit={{
            children: 'Request',
            id: 'btn_request-telehealth',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default TelehealthRequestContent;
